import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";

const EMPTY = { code: "", kind: "percent", value: "", expires_at: "", min_amount: "", max_uses: "" };
const field = "h-11 w-full rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm outline-none focus:border-[var(--gold)]";

const payload = (f) => ({
  code: f.code.trim().toUpperCase(), kind: f.kind, value: Number(f.value), active: f.active ?? true,
  expires_at: f.expires_at || null, min_amount: f.min_amount === "" || f.min_amount == null ? null : Number(f.min_amount),
  max_uses: f.max_uses === "" || f.max_uses == null ? null : Number(f.max_uses),
});

function NewCode({ onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", payload(f));
      toast.success(`Code ${f.code.toUpperCase()} créé`);
      setF(EMPTY);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} data-testid="promo-new-form" className="grid gap-4 rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6 sm:grid-cols-2 lg:grid-cols-6">
      <label className="text-xs text-[var(--muted)] lg:col-span-1">Code<input data-testid="promo-new-code" required value={f.code} onChange={set("code")} placeholder="ex. NOEL15" className={`${field} mt-1 uppercase`} /></label>
      <label className="text-xs text-[var(--muted)]">Type
        <select data-testid="promo-new-kind" value={f.kind} onChange={set("kind")} className={`${field} mt-1 bg-[var(--ink-2)]`}><option value="percent">Pourcentage (%)</option><option value="fixed">Montant fixe (€)</option></select>
      </label>
      <label className="text-xs text-[var(--muted)]">Remise<input data-testid="promo-new-value" required type="number" min="0.01" step="0.01" value={f.value} onChange={set("value")} placeholder={f.kind === "percent" ? "ex. 10" : "ex. 5"} className={`${field} mt-1`} /></label>
      <label className="text-xs text-[var(--muted)]">Fin (facultatif)<input data-testid="promo-new-expires" type="date" value={f.expires_at} onChange={set("expires_at")} className={`${field} mt-1`} /></label>
      <label className="text-xs text-[var(--muted)]">Achat minimum € (facultatif)<input data-testid="promo-new-min" type="number" min="0" step="0.01" value={f.min_amount} onChange={set("min_amount")} className={`${field} mt-1`} /></label>
      <label className="text-xs text-[var(--muted)]">Utilisations max (facultatif)<input data-testid="promo-new-max" type="number" min="1" step="1" value={f.max_uses} onChange={set("max_uses")} className={`${field} mt-1`} /></label>
      <button type="submit" data-testid="promo-new-submit" disabled={busy} className="flex h-11 items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-50 sm:col-span-2 lg:col-span-6 lg:w-fit">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Créer le code
      </button>
    </form>
  );
}

function Row({ p, onSaved }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = () => run(() => api.put(`/admin/promo-codes/${p.id}`, payload({ ...p, active: !p.active })), p.active ? `Code ${p.code} désactivé` : `Code ${p.code} activé`);
  const remove = () => { if (window.confirm(`Supprimer définitivement le code ${p.code} ?`)) run(() => api.delete(`/admin/promo-codes/${p.id}`), `Code ${p.code} supprimé`); };
  const limits = [p.expires_at ? `jusqu'au ${p.expires_at.slice(0, 10).split("-").reverse().join("/")}` : null, p.min_amount ? `dès ${euro(p.min_amount)}` : null, p.max_uses ? `${p.max_uses} utilisations max` : null].filter(Boolean).join(" · ") || "Sans limite";
  return (
    <tr data-testid={`promo-row-${p.code}`} className="border-b border-[var(--line)] text-sm">
      <td className="py-4 pr-4 font-semibold tracking-wider">{p.code}</td>
      <td className="py-4 pr-4 text-[var(--gold-soft)]">{p.kind === "percent" ? `-${p.value} %` : `-${euro(p.value)}`}</td>
      <td className="py-4 pr-4 text-[var(--muted)]">{limits}</td>
      <td data-testid={`promo-uses-${p.code}`} className="py-4 pr-4">{p.uses ?? 0}</td>
      <td className="py-4 pr-4">
        <button type="button" data-testid={`promo-toggle-${p.code}`} disabled={busy} onClick={toggle}
          className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-[0.18em] ${p.active ? "border-emerald-400/60 text-emerald-300" : "border-[var(--line)] text-[var(--muted)]"}`}>{p.active ? "Actif" : "Inactif"}</button>
      </td>
      <td className="py-4 text-right">
        <button type="button" data-testid={`promo-delete-${p.code}`} disabled={busy} onClick={remove} aria-label={`Supprimer ${p.code}`} className="text-[var(--muted)] hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export function PromoCodes() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-promo"], queryFn: async () => (await api.get("/admin/promo-codes")).data });
  const list = Array.isArray(data) ? data : [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-promo"] });
  return (
    <div data-testid="admin-promo-codes" className="space-y-8">
      <p className="max-w-2xl text-sm text-[var(--muted)]">Créez les codes à donner à vos clientes. Elles les saisissent sur la page de paiement, dans « Vous avez un code promo ? ». La remise s'applique sur les produits et les packs, pas sur la livraison. « Utilisations » compte les commandes payées avec le code.</p>
      <NewCode onSaved={refresh} />
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-[var(--gold)]" /> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead><tr className="border-b border-[var(--line)] text-xs uppercase tracking-[0.18em] text-[var(--muted)]"><th className="pb-3">Code</th><th className="pb-3">Remise</th><th className="pb-3">Conditions</th><th className="pb-3">Utilisations</th><th className="pb-3">Statut</th><th /></tr></thead>
            <tbody>{list.map((p) => <Row key={p.id} p={p} onSaved={refresh} />)}</tbody>
          </table>
          {!list.length ? <p data-testid="promo-empty" className="py-6 text-sm text-[var(--muted)]">Aucun code pour l'instant.</p> : null}
        </div>
      )}
    </div>
  );
}
